import { fhirClients } from "src-next/shared/api/fhirClient";
import { TELECONSULTORIA_CONTRACT_IDENTIFIER } from "../constants";
import {
  isAffiliationActive,
  organizationReferenceFromToken,
} from "../helpers/affiliationReferences";
import {
  searchProvidedAffiliations,
  searchReceivedAffiliations,
} from "./affiliationSearchService";

/** Erro de regra de negócio: a mensagem é mostrada como está para o usuário. */
export class AffiliationRuleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AffiliationRuleError";
  }
}

/**
 * A organização tem a extensão de Teleconsultoria contratada?
 * "unknown": a leitura falhou (ex.: sem permissão no Contract de outra
 * organização). Quem exige o contrato mostra um erro claro em vez de decidir às cegas.
 */
async function teleconsultoriaContractStatus(
  organizationToken: string,
): Promise<"active" | "missing" | "unknown"> {
  try {
    const bundle = await fhirClients.admin.search<fhir5.Contract>("Contract", {
      authority: organizationReferenceFromToken(organizationToken),
      status: "executed",
      identifier: TELECONSULTORIA_CONTRACT_IDENTIFIER,
      _count: "1",
    });
    return bundle.entry?.length ? "active" : "missing";
  } catch (error) {
    console.error("affiliationRules.teleconsultoriaContractStatus", error);
    return "unknown";
  }
}

interface AffiliationRuleParams {
  providerToken: string;
  receiverToken: string;
  /** Afiliação sendo reativada: não conta como "outra" afiliação. */
  ignoreAffiliationId?: string;
  /** Criação: bloqueia par já existente (mesmo inativo, deve ser reativado). */
  isNewPair: boolean;
  /**
   * Quem está pedindo (a organização logada):
   * - provider (padrão): a prestadora adiciona/reativa uma afiliada;
   * - receiver: a afiliada escolhe a prestadora — aí a prestadora precisa
   *   ter a extensão ativa. Também ajusta "Sua"/"Esta" organização nas mensagens.
   */
  requestedBy?: "provider" | "receiver";
}

/** "Sua organização" quando a regra fala de quem está logado; senão "Esta organização". */
function subject(isLoggedOrganization: boolean): string {
  return isLoggedOrganization ? "Sua organização" : "Esta organização";
}

/**
 * Regras da afiliação HIE/HIO (validadas antes de gravar):
 * 1. Não pode afiliar consigo mesma.
 * 2. Quem recebe teleconsultoria (afiliação ATIVA como receptora) não pode prestar.
 * 3. Quem já tem afiliadas (afiliação ATIVA como prestadora) não pode receber.
 *    Ter a extensão ativa NÃO bloqueia: a extensão vale para prestadora e afiliada.
 * 4. A receptora tem no máximo 1 afiliação ativa.
 * 5. Não duplica o mesmo par.
 * 6. Pedido da afiliada (`requestedBy: "receiver"`): a prestadora precisa ter a extensão ativa.
 */
export async function assertCanAffiliate({
  providerToken,
  receiverToken,
  ignoreAffiliationId,
  isNewPair,
  requestedBy = "provider",
}: AffiliationRuleParams): Promise<void> {
  const isReceiverRequest = requestedBy === "receiver";
  if (!providerToken || !receiverToken) {
    throw new AffiliationRuleError("Organização inválida.");
  }
  if (providerToken === receiverToken) {
    throw new AffiliationRuleError(
      "Uma organização não pode prestar teleconsultoria para si mesma.",
    );
  }

  const [providerAsReceiver, receiverAsReceiver, receiverAsProvider, providerContract] =
    await Promise.all([
      searchReceivedAffiliations(providerToken),
      searchReceivedAffiliations(receiverToken),
      searchProvidedAffiliations(receiverToken),
      isReceiverRequest ? teleconsultoriaContractStatus(providerToken) : undefined,
    ]);

  const isOtherActive = (affiliation: fhir5.OrganizationAffiliation) =>
    isAffiliationActive(affiliation) && affiliation.id !== ignoreAffiliationId;

  if (providerAsReceiver.affiliations.some(isOtherActive)) {
    throw new AffiliationRuleError(
      `${subject(!isReceiverRequest)} recebe teleconsultoria de outra prestadora e não pode prestar o serviço.`,
    );
  }
  if (receiverAsProvider.affiliations.some(isOtherActive)) {
    throw new AffiliationRuleError(
      `${subject(isReceiverRequest)} presta teleconsultoria para outras clínicas e não pode receber o serviço.`,
    );
  }
  if (providerContract === "missing") {
    throw new AffiliationRuleError(
      "Esta organização não tem a extensão de Teleconsultoria ativa e não pode prestar o serviço.",
    );
  }
  if (providerContract === "unknown") {
    throw new AffiliationRuleError(
      "Não foi possível confirmar se esta organização tem a extensão de Teleconsultoria ativa. Tente novamente.",
    );
  }

  const providerReference = organizationReferenceFromToken(providerToken);
  const isSameProvider = (affiliation: fhir5.OrganizationAffiliation) =>
    affiliation.organization?.reference === providerReference;

  if (isNewPair && receiverAsReceiver.affiliations.some(isSameProvider)) {
    throw new AffiliationRuleError(
      isReceiverRequest
        ? "Já existe um vínculo com esta prestadora. Peça a ela para reativá-lo."
        : "Esta organização já está na sua lista. Reative a afiliação existente.",
    );
  }

  const activeReceived = receiverAsReceiver.affiliations.find(isOtherActive);
  if (activeReceived) {
    if (isReceiverRequest) {
      throw new AffiliationRuleError(
        "Sua organização já recebe teleconsultoria de outra prestadora.",
      );
    }
    throw new AffiliationRuleError(
      isSameProvider(activeReceived)
        ? "Esta organização já é atendida pela sua organização."
        : "Esta organização já recebe teleconsultoria de outra prestadora.",
    );
  }
}
